import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  StepComponent,
  StepperComponent,
  StepFooterComponent,
  MibButtonComponent,
  MibIconsModule,
} from '@medinbox/angular';
import { PrecallInfoStepComponent } from './stepper/precall-info-step/precall-info-step.component';
import { PrecallConectivityStepComponent } from './stepper/precall-conectivity-step/precall-conectivity-step.component';
import { PrecallQualityStepComponent } from './stepper/precall-quality-step/precall-quality-step.component';
import { PrecallReportStepComponent } from './stepper/precall-report-step/precall-report-step.component';
import { ConnectivityTestService } from '../../core/services/connectivity-test.service';
import { Observable, of, switchMap } from 'rxjs';
import { ProcallConnectivityStep as PrecallConnectivityStep } from '../../models/app/precall/precall-connectivity-step';
import { ReportService } from '../../core/services/report.service';
import { PrecallQualityResult } from '../../models/app/precall/precall-quality-result';
import { environment } from '../../../environments/environment';
import { MicrophoneService } from '../../core/services/microphone.service';
import { AudioInputDevice } from '@opentok/client';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EnvironmentDetectionService } from 'src/app/shared/services/environment-detection.service';
import { BrowserNotSupportedComponent } from './stepper/browser-not-supported/browser-not-supported.component';
import { PlatformType } from 'src/libs/browser-compat';
import { ActivatedRoute } from '@angular/router';
import { PrecallSettings } from 'src/app/models/app/precall-settings';

@Component({
  selector: 'app-precall-page',
  imports: [
    StepperComponent,
    StepComponent,
    PrecallInfoStepComponent,
    PrecallConectivityStepComponent,
    PrecallQualityStepComponent,
    PrecallReportStepComponent,
    BrowserNotSupportedComponent,
    StepFooterComponent,
    MibButtonComponent,
    MibIconsModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './precall-page.component.html',
})
export class PrecallPageComponent implements OnInit {
  private readonly connectivityService = inject(ConnectivityTestService);
  private readonly micService = inject(MicrophoneService);
  private readonly reportService = inject(ReportService);
  private readonly envDetectionService = inject(EnvironmentDetectionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  readonly routeSettings = toSignal(this.route.data as Observable<PrecallSettings>);
  readonly currentStep = signal(0);
  readonly connectivityTestResult = signal<PrecallConnectivityStep | undefined>(undefined);
  readonly qualityResult = signal<PrecallQualityResult | undefined>(undefined);
  readonly micPermissionState = signal<PermissionState | null>(null);

  readonly connectivityStep = viewChild<PrecallConnectivityStep>('connectivityStep');
  readonly qualityStep = viewChild<PrecallQualityStepComponent>('qualityStep');
  readonly reportStep = viewChild<PrecallReportStepComponent>('reportStep');

  readonly isBrowserSupported = computed(() => this.envDetectionService.isSupported());
  readonly micPermission = computed(
    () =>
      this.micPermissionState() === 'granted' || this.micService.store.snapshot.permissionGranted,
  );
  readonly reportMailTo = computed(() => {
    const reportValue = this.reportService.report();
    if (!reportValue) return '';

    const subject = encodeURIComponent('Connectivity & Quality Report');
    const content = encodeURIComponent(this.reportService.buildEmailReport(reportValue));
    return `mailto:${environment.supportEmail}?subject=${subject}&body=${content}`;
  });
  readonly isAndroid = computed(
    () => this.envDetectionService.deviceInfo()?.platform === PlatformType.android,
  );

  ngOnInit(): void {
    this.connectivityService.store.data$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.connectivityTestResult.set(result));

    this.micService
      .onPermissionChange()
      .pipe(
        switchMap((state) => this.onPermissionChange(state)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected handleStepperChange(event: number) {
    this.currentStep.set(event);
    this.gotoTop();

    switch (event) {
      case 1:
        this.onConnectivityStep();
        break;
      case 2:
        this.onQualityTest();
        break;
      case 3:
        this.onReportTest();
        break;
    }
  }

  protected onConnectivityStep() {
    const result = this.connectivityTestResult();
    if (!result) {
      console.warn('Connectivity tests is not initialized');
      return;
    }
    if (result?.status === 'not_started') {
      this.connectivityService.start();
    }
  }

  protected onConnectivityStepRetry() {
    const result = this.connectivityTestResult();
    if (!result) {
      console.warn('Connectivity tests is not initialized');
      return;
    }
    if (['success', 'error'].includes(result.status)) {
      this.connectivityService.start();
    }
  }

  protected onQualityTest() {
    const qualityStep = this.qualityStep();

    if (!qualityStep) {
      console.error('Can not start precall. Can not find PrecallQualityStep component');
      return;
    }

    if (!this.qualityResult() || this.qualityResult()?.status === 'not_started') {
      qualityStep.start();
    }
  }

  protected onQualityTestRetry() {
    const qualityStep = this.qualityStep();

    if (!qualityStep) {
      console.error('Can not start precall. Can not find PrecallQualityStep component');
      return;
    }

    if (
      this.qualityResult() &&
      ['success', 'failed'].includes(this.qualityResult()?.status || 'failed')
    ) {
      qualityStep.start();
    }
  }

  protected onReportTest() {
    const reportStep = this.reportStep();

    if (!reportStep) {
      console.error('Can not start precall. Can not find PrecallReportStep component');
      return;
    }

    reportStep.start();
  }

  protected canDisplayTerminate(): boolean {
    // return window && window.opener;
    return true;
  }

  protected gotoTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  protected onClickTerminate(event: MouseEvent) {
    event.preventDefault();
    // Prevent SSR mode (window does not exist in SSR)
    if (window && window.close) window.close();
  }

  protected onPermissionChange(state: PermissionState): Observable<AudioInputDevice[] | null> {
    console.log(`Microphone permission state changed: ${state}`);
    this.micPermissionState.set(state);
    if (state !== 'granted') {
      this.micService.store.updateState({ permissionGranted: false });
    }
    return state === 'granted' && !this.micService.store.snapshot.streamActive
      ? this.micService.requestPermission$()
      : of(null);
  }

  protected requestMicrophonePermission() {
    this.micService.requestPermission$().subscribe();
  }
}
